import type { MedplumClient } from '@medplum/core';
import type { Appointment, Task } from '@medplum/fhirtypes';
import {
  APPOINTMENT_REQUEST_CATEGORY,
  APPOINTMENT_RESCHEDULE_CATEGORY,
  parseAppointmentRequestDetails,
} from '../../messaging/appointmentRequestMessage';
import { respondPortalRescheduleViaBot } from './portalRescheduleBot';
import {
  finalizeAppointmentRequest,
  findPendingAppointmentRequestTaskForProposed,
} from '../utils/appointmentRequestTask';
import {
  finalizeRescheduleRequest,
  findPendingRescheduleTaskForAppointment,
} from '../utils/rescheduleRequestTask';
import { PORTAL_RESCHEDULE_ORIGINAL_APPOINTMENT_EXT } from '../utils/portalRescheduleHold';

function communicationIdFromTask(task: Task | undefined): string | undefined {
  const ref = task?.reasonReference?.reference;
  if (!ref?.startsWith('Communication/')) return undefined;
  return ref.slice('Communication/'.length);
}

/**
 * Bot-created request Tasks carry no reasonReference, so locate the patient's request message
 * (newest first) whose details payload points at this proposed hold.
 */
async function findRequestCommunicationId(
  medplum: MedplumClient,
  proposed: Appointment,
): Promise<string | undefined> {
  const patientRef = proposed.participant?.find((p) => p.actor?.reference?.startsWith('Patient/'))?.actor?.reference;
  if (!patientRef || !proposed.id) return undefined;
  const candidates = await medplum.searchResources('Communication', {
    category: [APPOINTMENT_REQUEST_CATEGORY, APPOINTMENT_RESCHEDULE_CATEGORY]
      .map(({ system, code }) => `${system}|${code}`)
      .join(','),
    sender: patientRef,
    _sort: '-sent',
    _count: '50',
  });
  return candidates.find((c) => parseAppointmentRequestDetails(c)?.proposedAppointmentId === proposed.id)?.id;
}

function isBookedFhirStatus(status: Appointment['status'] | undefined): boolean {
  return status === 'booked' || status === 'pending';
}

function portalDecisionAlreadyApplied(
  proposed: Appointment,
  decision: 'accept' | 'deny',
): boolean {
  if (decision === 'accept') {
    return isBookedFhirStatus(proposed.status);
  }
  return proposed.status === 'cancelled';
}

function isRescheduleProposedHold(proposed: Appointment): boolean {
  return proposed.extension?.some((e) => e.url === PORTAL_RESCHEDULE_ORIGINAL_APPOINTMENT_EXT) ?? false;
}

/** Shared accept/deny path for portal appointment requests (messages UI + calendar sync). */
export async function completePortalRescheduleDecision(
  medplum: MedplumClient,
  params: {
    proposedAppointmentId: string;
    originalAppointmentId?: string;
    decision: 'accept' | 'deny';
    communicationId?: string;
  },
): Promise<void> {
  const proposed = await medplum.readResource('Appointment', params.proposedAppointmentId);
  if (!portalDecisionAlreadyApplied(proposed, params.decision)) {
    await respondPortalRescheduleViaBot(medplum, {
      proposedAppointmentId: params.proposedAppointmentId,
      decision: params.decision,
    });
  }

  let communicationId = params.communicationId;
  const originalAppointmentId = params.originalAppointmentId?.trim();
  const isReschedule = Boolean(originalAppointmentId) || isRescheduleProposedHold(proposed);

  if (!communicationId) {
    const task = isReschedule && originalAppointmentId
      ? await findPendingRescheduleTaskForAppointment(medplum, originalAppointmentId)
      : await findPendingAppointmentRequestTaskForProposed(medplum, params.proposedAppointmentId);
    communicationId = communicationIdFromTask(task)
      ?? await findRequestCommunicationId(medplum, proposed);
  }

  if (isReschedule && originalAppointmentId) {
    await finalizeRescheduleRequest(medplum, {
      communicationId,
      decision: params.decision,
      originalAppointmentId,
    });
    return;
  }

  await finalizeAppointmentRequest(medplum, {
    communicationId,
    decision: params.decision,
    proposedAppointmentId: params.proposedAppointmentId,
  });
}
